import type { ReactNode, RefCallback } from 'react';

import { SecondaryNavMobileSlot } from '@/components/layout/SecondarySidebarSlot';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

interface TopbarShellProps {
  portalMode: boolean;
  showMenuButton: boolean;
  onMenuToggle?: (() => void) | undefined;
  className?: string | undefined;
  setPortalNode: RefCallback<HTMLDivElement>;
  content: ReactNode;
}

export function TopbarShell({
  portalMode,
  showMenuButton,
  onMenuToggle,
  className,
  setPortalNode,
  content,
}: TopbarShellProps) {
  return (
    <header
      className={cn(
        'flex shrink-0 gap-2.5 px-3 sm:gap-4 sm:px-4 lg:px-6',
        portalMode ? 'min-h-14 items-center py-1.5' : 'h-14 items-center',
        'border-b border-border bg-background',
        'supports-[padding:env(safe-area-inset-top)]:pt-[env(safe-area-inset-top)]',
        // On a phone a topbar that grows to several rows keeps its own top padding under the notch.
        portalMode &&
          'max-sm:supports-[padding:env(safe-area-inset-top)]:pt-[max(0.375rem,env(safe-area-inset-top))]',
        className,
      )}
    >
      {showMenuButton ? (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Открыть меню"
          onClick={onMenuToggle}
          // In a topbar that wraps to several rows on a phone the menu stays on the first one.
          className={cn('flex-shrink-0 max-sm:size-10 lg:hidden', portalMode && 'max-sm:self-start')}
        >
          <MenuIcon />
        </Button>
      ) : null}

      {portalMode ? (
        <div
          ref={setPortalNode}
          className="flex min-w-0 flex-1 items-center"
          data-testid="topbar-portal-slot"
        />
      ) : (
        content
      )}
      <SecondaryNavMobileSlot />
    </header>
  );
}

function MenuIcon() {
  return (
    <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
      />
    </svg>
  );
}
